import React, { useEffect, useRef } from "react";
import { Input, Select, Text } from "components-lib";
import EnhancedAutocompleteInput from "../EnhancedAutocompleteInput";
import { useDoctorRecommendations } from "../hooks/useDoctorRecommendations";
import {
  saveToStorage,
  getStoredSuggestions,
  STORAGE_KEYS,
} from "../utils/storageUtils";

/**
 * ComplaintsSection Component
 * Manages patient complaints with severity, duration, and date tracking
 * Provides enhanced autocomplete functionality for complaints and severity levels
 * Fetches suggestions from API and allows custom input
 */
/*
  Note: This component consumes `templateData` in a useEffect and merges
  `templateData.complaints` into the `complaints` array using `setComplaints`.
  It performs dedupe by complaint text and by comparing non-empty fields.

  See `docs/LOAD_TEMPLATE_FLOW.md` for the full flow.
*/
const ComplaintsSection = ({
  templateData,
  complaints,
  setComplaints,
  newComplaint,
  setNewComplaint,
  severitySuggestions,
  setSeveritySuggestions,
  doctorRecommendations,
  doctor_id,
  appointmentId,
  appointmentPrescription,
}) => {
  // Use the doctor recommendations hook for API-based suggestions
  const {
    recommendations,
    getSeverityOptions,
    getComplaintSuggestions,
    getSeverityForComplaint,
  } = useDoctorRecommendations(doctor_id);

  const hydratedFromPrescriptionRef = useRef(null);

  // Handle complaint selection from autocomplete
  const handleComplaintSelect = (value, metadata) => {
    setNewComplaint((prev) => ({
      ...prev,
      complaint: value,
    }));

    // Auto-fill severity if available from API data
    if (metadata?.isFromSuggestions) {
      const associatedSeverity = getSeverityForComplaint(value);
      if (associatedSeverity) {
        setNewComplaint((prev) => ({
          ...prev,
          severity: associatedSeverity,
        }));
      }
    }

    // Save to localStorage for future suggestions
    saveToStorage(STORAGE_KEYS.COMPLAINTS, value);
  };

  // Handle complaint input change
  const handleComplaintChange = (e) => {
    const value = e.target.value;
    setNewComplaint((prev) => ({
      ...prev,
      complaint: value,
    }));
  };

  // Handle severity selection from autocomplete
  const handleSeveritySelect = (value, metadata) => {
    setNewComplaint((prev) => ({
      ...prev,
      severity: value,
    }));

    // Save to localStorage for future suggestions
    saveToStorage(STORAGE_KEYS.SEVERITY, value);
  };

  // Handle severity input change
  const handleSeverityChange = (e) => {
    const value = e.target.value;
    setNewComplaint((prev) => ({
      ...prev,
      severity: value,
    }));
  };

  // Add new complaint to the list
  const handleAddComplaint = () => {
    if (newComplaint.complaint) {
      setComplaints([
        ...complaints,
        { ...newComplaint, no: complaints.length + 1 },
      ]);
      saveToStorage(STORAGE_KEYS.COMPLAINTS, newComplaint.complaint);
      saveToStorage(STORAGE_KEYS.SEVERITY, newComplaint.severity);
      setNewComplaint({
        no: "",
        complaint: "",
        severity: "",
        duration: "",
        date: "",
      });
      setSeveritySuggestions([]);
    }
  };

  // Delete complaint from the list
  const handleDeleteComplaint = (index) => {
    setComplaints(complaints.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!appointmentId || !appointmentPrescription) return;
    if (hydratedFromPrescriptionRef.current === appointmentId) return;

    try {
      const source = appointmentPrescription.data || appointmentPrescription;
      const apiComplaints = Array.isArray(source?.complaints)
        ? source.complaints
        : Array.isArray(source?.prescription?.complaints)
          ? source.prescription.complaints
          : [];

      apiComplaints.forEach((apiComplaint) => {
        if (!apiComplaint?.complaint) return;

        const existsInComplaints = complaints.some(
          (complaint) =>
            (complaint.complaint || "").toLowerCase() ===
            (apiComplaint.complaint || "").toLowerCase(),
        );

        if (!existsInComplaints) {
          setNewComplaint({
            no: "",
            complaint: apiComplaint.complaint || "",
            severity: apiComplaint.severity || "",
            duration: apiComplaint.duration || "",
            date: apiComplaint.date || "",
          });

          setComplaints((prevComplaints = []) => [
            ...prevComplaints,
            {
              ...apiComplaint,
              no: prevComplaints.length + 1,
            },
          ]);

          saveToStorage(STORAGE_KEYS.COMPLAINTS, apiComplaint.complaint);
          saveToStorage(STORAGE_KEYS.SEVERITY, apiComplaint.severity);
          setNewComplaint({
            no: "",
            complaint: "",
            severity: "",
            duration: "",
            date: "",
          });
          setSeveritySuggestions([]);
        }
      });

      hydratedFromPrescriptionRef.current = appointmentId;
    } catch (error) {
      console.error("Error hydrating complaints from prescription:", error);
    }
  }, [appointmentId, appointmentPrescription, complaints, setComplaints, setNewComplaint, setSeveritySuggestions]);

  useEffect(() => {
    if (!templateData) return;

    try {
      let parsed = templateData;
      if (typeof templateData === "string") {
        parsed = JSON.parse(templateData);
      }

      const templateComplaints = Array.isArray(parsed.complaints)
        ? parsed.complaints
        : Array.isArray(parsed)
        ? parsed
        : [];

      const normalized = templateComplaints
        .map((c) => ({
          no: c.no ?? "",
          complaint: (c.complaint ?? "").toString().trim(),
          severity: c.severity ?? "",
          duration: c.duration ?? "",
          date: c.date ?? "",
        }))
        .filter((c) => c && c.complaint);

      if (normalized.length === 0) return;

      setComplaints((prev = []) => {
        const existing = Array.isArray(prev) ? prev : [];

        const toAdd = [];

        const isDuplicateAgainst = (candidate, target) => {
          // match by complaint text case-insensitive
          if (
            target.complaint &&
            candidate.complaint &&
            target.complaint.toLowerCase() === candidate.complaint.toLowerCase()
          ) {
            return true;
          }

          // match when all non-empty fields are equal (complaint/severity/duration/date)
          const fields = ["complaint", "severity", "duration", "date"];
          const allMatch = fields.every((f) => {
            const a = (target[f] ?? "").toString().trim().toLowerCase();
            const b = (candidate[f] ?? "").toString().trim().toLowerCase();
            // if candidate has no value for the field, ignore it in this comparison
            if (!b) return true;
            return a === b;
          });
          return allMatch;
        };

        normalized.forEach((tc) => {
          const existsInExisting = existing.some((ec) => isDuplicateAgainst(tc, ec));
          const existsInToAdd = toAdd.some(
            (added) => added.complaint.toLowerCase() === tc.complaint.toLowerCase()
          );

          if (!existsInExisting && !existsInToAdd) {
            toAdd.push({
              ...tc,
              no: existing.length + toAdd.length + 1,
            });

            // persist common suggestion keys
            try {
              saveToStorage(STORAGE_KEYS.COMPLAINTS, tc.complaint);
              saveToStorage(STORAGE_KEYS.SEVERITY, tc.severity);
            } catch (e) {
              // ignore storage errors
            }
          }
        });

        if (toAdd.length === 0) return existing;
        // clear newComplaint and severity suggestions similar to API flow
        setNewComplaint({
          no: "",
          complaint: "",
          severity: "",
          duration: "",
          date: "",
        });
        setSeveritySuggestions && setSeveritySuggestions([]);
        return [...existing, ...toAdd];
      });
    } catch (err) {
      console.warn("Failed to parse/apply templateData complaints:", err);
    }
  }, [templateData, setComplaints, setNewComplaint, setSeveritySuggestions]);

  return (
    <tr>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <Text fontWeight="bold" style={{ fontSize: "16px" }}>
          Complaints:
        </Text>
      </td>
      <td
        style={{
          border: "1px solid #E2E8F0",
          padding: "10px",
          background: "white",
          textAlign: "left",
        }}
      >
        <table width="100%" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Complaint
              </th>
              <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Severity
              </th>
              <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Duration
              </th>
              {/* <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Start Date
              </th> */}
              <th
                style={{
                  border: "1px solid #E2E8F0",
                  padding: "5px",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Render existing complaints */}
            {complaints.map((complaint, index) => (
              <tr key={index}>
                {/* <td style={{ border: "1px solid #E2E8F0", padding: "5px" }}>
                  {complaint.no}
                </td> */}
                <td style={{ border: "1px solid #E2E8F0", padding: "5px" }}>
                  {complaint.complaint}
                </td>
                <td style={{ border: "none", padding: "6px" }}>
                  {complaint.severity}
                </td>
                <td style={{ border: "none", padding: "6px" }}>
                  {complaint.duration}
                </td>
                {/* <td style={{ border: "1px solid #E2E8F0", padding: "5px" }}>
                  {complaint.date}
                </td> */}
                <td style={{ border: "none", padding: "6px" }}>
                  <button
                    style={{
                      backgroundColor: "#E2E8F0",
                      color: "red",
                      fontWeight: "bold",
                      border: "none",
                      borderRadius: "12px",
                      cursor: "pointer",
                      padding: "6px 10px",
                      fontSize: "12px",
                    }}
                    onClick={() => handleDeleteComplaint(index)}
                  >
                    X
                  </button>
                </td>
              </tr>
            ))}

            {/* Input row for new complaint */}
            <tr>
              {/* <td style={{ border: "1px solid #E2E8F0", padding: "5px" }}></td> */}
              <td
                style={{
                  border: "none",
                  padding: "6px",
                  position: "relative",
                  background: "white",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                  borderRadius: "10px",
                }}
              >
                <EnhancedAutocompleteInput
                  value={newComplaint.complaint}
                  onChange={handleComplaintChange}
                  onSelect={handleComplaintSelect}
                  placeholder="Enter complaint..."
                  suggestions={[
                    ...getComplaintSuggestions(),
                    ...getStoredSuggestions(STORAGE_KEYS.COMPLAINTS),
                  ]}
                  allowCustomInput={true}
                  maxSuggestions={8}
                  minInputLength={1}
                  highlightMatch={true}
                  onCustomInput={(value) =>
                    saveToStorage(STORAGE_KEYS.COMPLAINTS, value)
                  }
                  style={{
                    width: "100%",
                    padding: "5px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                  }}
                />
              </td>
              <td
                style={{
                  border: "none",
                  padding: "6px",
                  position: "relative",
                  background: "white",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                  borderRadius: "10px",
                }}
              >
                <EnhancedAutocompleteInput
                  value={newComplaint.severity}
                  onChange={handleSeverityChange}
                  onSelect={handleSeveritySelect}
                  placeholder="Enter severity..."
                  suggestions={[
                    ...getSeverityOptions(),
                    ...getStoredSuggestions(STORAGE_KEYS.SEVERITY),
                  ]}
                  allowCustomInput={true}
                  maxSuggestions={6}
                  minInputLength={1}
                  highlightMatch={true}
                  onCustomInput={(value) =>
                    saveToStorage(STORAGE_KEYS.SEVERITY, value)
                  }
                  style={{
                    width: "100%",
                    padding: "5px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                  }}
                />
              </td>
              <td
                style={{
                  border: "none",
                  padding: "6px",
                  position: "relative",
                  background: "white",
                  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                  borderRadius: "10px",
                }}
              >
                <Select
                  placeholder="Select Duration"
                  value={newComplaint.duration}
                  onChange={(e) =>
                    setNewComplaint({
                      ...newComplaint,
                      duration: e.target.value,
                    })
                  }
                  style={{
                    width: "80%",
                    padding: "5px 30px 5px 5px",
                    margin: "0 5px 0 0",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="1-2 hour">1-2 hour</option>
                  <option value="less than one day">less than one day</option>
                  <option value="1-2 days">1-2 days</option>
                  <option value="3-5 days">3-5 days</option>
                  <option value="1 week">1 week</option>
                  <option value="2 weeks">2 weeks</option>
                  <option value="3 weeks">3 weeks</option>
                  <option value="1 month">1 month</option>
                  <option value="2 months">2 months</option>
                  <option value="3 months">3 months</option>
                  <option value="4 months">4 months</option>
                  <option value="5 months">5 months</option>
                  <option value="6 months">6 months</option>
                </Select>
              </td>
              {/* <td style={{ border: "1px solid #E2E8F0", padding: "5px" }}>
                <Input
                  type="date"
                  value={newComplaint.date}
                  onChange={(e) =>
                    setNewComplaint({ ...newComplaint, date: e.target.value })
                  }
                  style={{
                    padding: "5px",
                    margin: "0 5px 0 0",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                  }}
                />
              </td> */}
              <td style={{ border: "none", padding: "6px" }}>
                <button
                  style={{
                    backgroundColor: "#4CAF50",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    padding: "6px 12px",
                    fontSize: "12px",
                  }}
                  onClick={handleAddComplaint}
                >
                  Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  );
};

export default ComplaintsSection;
